import React, { useState } from 'react';
import { read, utils } from 'xlsx';
import { toast } from 'react-toastify';
import {
  ArrowUpTrayIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  PlusCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../services/api';

// Alias de cabeceras aceptadas (case-insensitive) → campo interno. El expediente
// se identifica por CLIENTE (clientId) + referencia interna del cliente.
const ALIASES = {
  clientId: ['clientid', 'client_id', 'client id', 'cliente', 'id_cliente', 'id cliente'],
  client_internal_reference: [
    'client_internal_reference',
    'ref cliente',
    'ref_cliente',
    'referencia',
    'referencia cliente',
    'referencia interna',
    'referencia_cliente',
    'internal reference',
    'internal_reference',
    'ref',
  ],
  title: ['title', 'patron', 'patrón', 'patron de texto', 'patrón de texto', 'patron_texto', 'concepto', 'titulo', 'título'],
  amount: ['amount', 'importe', 'importe final', 'importe_final', 'importe a actualizar', 'cantidad', 'importe_deuda'],
};

const pick = (rowMap, keys) => {
  for (const k of keys) {
    if (rowMap[k] !== undefined && rowMap[k] !== '') return rowMap[k];
  }
  return undefined;
};

const parseRows = (json) =>
  json.map((row, idx) => {
    const map = {};
    Object.keys(row).forEach((k) => {
      map[String(k).trim().toLowerCase()] = row[k];
    });
    const clientIdRaw = pick(map, ALIASES.clientId);
    const refRaw = pick(map, ALIASES.client_internal_reference);
    const titleRaw = pick(map, ALIASES.title);
    const amountRaw = pick(map, ALIASES.amount);
    const amount =
      amountRaw === undefined ? undefined : Number(String(amountRaw).replace(/\s/g, '').replace(',', '.'));

    const clientId = clientIdRaw ? String(clientIdRaw).trim() : '';
    const clientRef = refRaw !== undefined && refRaw !== '' ? String(refRaw).trim() : '';
    const title = titleRaw ? String(titleRaw).trim() : '';
    const errors = [];
    if (!/^[a-f0-9]{24}$/i.test(clientId)) errors.push('clientId inválido');
    if (!clientRef) errors.push('referencia vacía');
    if (!title) errors.push('title (patrón) vacío');
    if (amount === undefined || Number.isNaN(amount) || amount < 0) errors.push('importe inválido');

    return {
      rowNumber: idx + 2,
      clientId,
      client_internal_reference: clientRef,
      title,
      amount,
      valid: errors.length === 0,
      errors,
    };
  });

const STATUS_META = {
  updated: { label: 'Actualizado', cls: 'text-emerald-700 bg-emerald-50', Icon: CheckCircleIcon },
  created: { label: 'Creado', cls: 'text-blue-700 bg-blue-50', Icon: PlusCircleIcon },
  error: { label: 'Error', cls: 'text-red-700 bg-red-50', Icon: ExclamationTriangleIcon },
};

const InterestImportTab = () => {
  const [rows, setRows] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [report, setReport] = useState(null);

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      toast.error('Selecciona un archivo Excel válido (.xlsx, .xls)');
      return;
    }
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = (ev) => {
      try {
        const wb = read(ev.target.result, { type: 'buffer' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = utils.sheet_to_json(ws);
        const parsed = parseRows(json);
        setRows(parsed);
        setReport(null);
        toast.success(`Archivo leído: ${parsed.length} filas`);
      } catch (err) {
        toast.error('Error al leer el archivo Excel');
      }
    };
    e.target.value = '';
  };

  const validItems = rows.filter((r) => r.valid);

  const handleSubmit = async () => {
    if (validItems.length === 0) {
      toast.error('No hay filas válidas para actualizar');
      return;
    }
    setIsProcessing(true);
    try {
      const payload = {
        items: validItems.map((r) => ({
          clientId: r.clientId,
          client_internal_reference: r.client_internal_reference,
          title: r.title,
          amount: r.amount,
        })),
      };
      const { data } = await api.post('/expedients/interest-import', payload);
      setReport(data.data);
      const { updated = 0, created = 0, error = 0 } = data.data.summary || {};
      toast.success(`Hecho: ${updated} actualizados, ${created} creados, ${error} errores`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div>
      {/* Instrucciones + campos del Excel */}
      <div className="mb-6 p-5 bg-blue-50 border border-blue-100 rounded-lg">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0" />
          <div className="text-sm text-slate-700">
            <p className="font-semibold text-slate-800 mb-1">Actualización de intereses</p>
            <p className="mb-3">
              Sube un Excel para <strong>sustituir el importe</strong> del concepto de interés en cada
              expediente. El expediente se localiza por <strong>cliente + referencia interna</strong>; luego se
              busca el concepto por su título y se actualiza su importe (la deuda total se recalcula).
            </p>

            <p className="font-semibold text-slate-800 mb-1">Columnas que debe tener el Excel:</p>
            <ul className="list-disc list-inside space-y-1 mb-3">
              <li>
                <code className="px-1 bg-white rounded-lg border border-blue-100">clientId</code> — ID del cliente
                (24 caracteres).
              </li>
              <li>
                <code className="px-1 bg-white rounded-lg border border-blue-100">client_internal_reference</code> —
                referencia interna del expediente para ese cliente (la &quot;Ref Cliente&quot;).
              </li>
              <li>
                <code className="px-1 bg-white rounded-lg border border-blue-100">title</code> — patrón de
                texto exacto del concepto a actualizar (p.ej. <em>Interés de penalización</em>).
              </li>
              <li>
                <code className="px-1 bg-white rounded-lg border border-blue-100">amount</code> — importe
                final (número, sin símbolo €; usa punto o coma decimal).
              </li>
            </ul>

            <p className="text-xs text-slate-500">
              El expediente se resuelve por cliente + referencia; si no se encuentra (o hay varios), esa fila se
              marca como error. El título del concepto se compara exacto (ignorando mayúsculas/espacios); si hay
              varios con el mismo título se actualiza el más reciente, y si no existe se crea como deuda.
            </p>
          </div>
        </div>
      </div>

      {/* Subida */}
      <div className="relative rounded-lg border-2 border-dashed border-slate-200 hover:border-slate-300 bg-slate-50 mb-6">
        <input
          type="file"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          onChange={handleFileInput}
          accept=".xlsx,.xls"
        />
        <div className="p-8 text-center">
          <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-slate-400 mb-3" />
          <p className="text-sm text-slate-600">
            Arrastra tu Excel aquí o <span className="text-blue-600 font-medium">haz clic para seleccionar</span>
          </p>
          <p className="text-xs text-slate-500 mt-1">Formatos soportados: .xlsx, .xls</p>
        </div>
      </div>

      {/* Previsualización + acción */}
      {rows.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-600">
              {rows.length} filas — <span className="text-emerald-600 font-medium">{validItems.length} válidas</span>
              {rows.length - validItems.length > 0 && (
                <span className="text-red-600 font-medium"> · {rows.length - validItems.length} con errores</span>
              )}
            </span>
            <button
              onClick={handleSubmit}
              disabled={isProcessing || validItems.length === 0}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors ${
                isProcessing || validItems.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isProcessing ? 'Procesando...' : 'Actualizar intereses'}
            </button>
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
            <table className="table-auto w-full text-sm">
              <thead className="text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-3 text-left">Fila</th>
                  <th className="px-3 py-3 text-left">clientId</th>
                  <th className="px-3 py-3 text-left">Ref cliente</th>
                  <th className="px-3 py-3 text-left">title</th>
                  <th className="px-3 py-3 text-left">amount</th>
                  <th className="px-3 py-3 text-left">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rows.map((r) => (
                  <tr key={r.rowNumber} className={r.valid ? '' : 'bg-red-50'}>
                    <td className="px-3 py-2 text-slate-500">{r.rowNumber}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-800">{r.clientId || '—'}</td>
                    <td className="px-3 py-2 text-slate-800">{r.client_internal_reference || '—'}</td>
                    <td className="px-3 py-2 text-slate-800">{r.title || '—'}</td>
                    <td className="px-3 py-2 text-slate-800">
                      {Number.isNaN(r.amount) || r.amount === undefined ? '—' : r.amount}
                    </td>
                    <td className="px-3 py-2">
                      {r.valid ? (
                        <span className="text-emerald-600">Válida</span>
                      ) : (
                        <span className="text-red-600">{r.errors.join(', ')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reporte del backend */}
      {report && (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 text-sm font-medium text-slate-700">
            Resultado: {report.summary.updated} actualizados · {report.summary.created} creados ·{' '}
            {report.summary.error} errores
          </div>
          <div className="overflow-x-auto">
            <table className="table-auto w-full text-sm">
              <thead className="text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-3 text-left">clientId</th>
                  <th className="px-3 py-3 text-left">Ref cliente</th>
                  <th className="px-3 py-3 text-left">title</th>
                  <th className="px-3 py-3 text-left">Estado</th>
                  <th className="px-3 py-3 text-left">Nueva deuda</th>
                  <th className="px-3 py-3 text-left">Detalle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {report.results.map((r, i) => {
                  const meta = STATUS_META[r.status] || STATUS_META.error;
                  const Icon = meta.Icon;
                  return (
                    <tr key={`${r.clientId}-${r.client_internal_reference}-${i}`}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-800">{r.clientId || '—'}</td>
                      <td className="px-3 py-2 text-slate-800">{r.client_internal_reference || '—'}</td>
                      <td className="px-3 py-2 text-slate-800">{r.title || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium ${meta.cls}`}>
                          <Icon className="w-4 h-4" />
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-800">
                        {r.importe_deuda !== undefined ? `${r.importe_deuda} €` : '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{r.reason || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterestImportTab;
